import React from 'react';
import { Text, Button } from 'v3/components';
import CircularProgress from '@mui/material/CircularProgress';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { safeStr } from '../Utils/helper';
import { styles } from '../styles';

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

export default function VideoStep({
  answered,
  currentVideoIndex,
  handleBackToIdentification,
  handleContinueAfterVideo,
  handlePlaybackRateChange,
  handleStartVideoFromOverlay,
  handleToggleFullscreen,
  handleToggleMute,
  handleToggleVideoPlayback,
  handleVideoCanPlay,
  handleVideoEnded,
  handleVideoError,
  handleVideoLoadStart,
  handleVideoLoadedData,
  handleVideoLoadedMetadata,
  handleVideoSeeking,
  handleVideoSurfaceClick,
  handleVideoSuspend,
  handleVideoTimeUpdate,
  handleVideoVolumeChange,
  handleVideoWaiting,
  handleVolumeChange,
  hasOnlyVideoTraining,
  headerTitle,
  isLastVideo,
  isMobile,
  isNativeIosSafariVideo,
  isVideoFullscreen,
  isVideoPlaying,
  model,
  revealFullscreenControls,
  setIsVideoPlaying,
  showFullscreenControls,
  showVideoFallbackCover,
  total,
  totalVideos,
  videoCurrentTime,
  videoGateCompleted,
  videoLoadFailed,
  videoLoading,
  videoMuted,
  videoPlaybackRate,
  videoProgressLabel,
  videoProgressPct,
  videoRef,
  videoReviewMode,
  videoShellRef,
  videoTimeLabel,
  videoVolume,
  watchOnlyVideoQuestionItem,
  watchOnlyVideoUrl,
}) {
  if (!watchOnlyVideoQuestionItem) return null;

  return (
    <div style={styles.videoGateCard}>
      <div style={styles.videoGateHeader}>
        <div style={styles.videoGateHeaderLeft}>
          <div style={styles.videoGateIconBox}>
            <PlayCircleOutlineOutlinedIcon
              style={{ fontSize: 22, color: '#2563EB' }}
            />
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={styles.videoGateTitle}>{headerTitle}</div>
            <div style={styles.videoGateSubtitle}>
              {safeStr(model?.description).trim() ||
                'Assista ao conteúdo obrigatório para continuar.'}
            </div>
            {totalVideos > 1 && (
              <div style={styles.videoCounterText}>
                Vídeo {currentVideoIndex + 1} de {totalVideos}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={styles.videoGateDivider} />

      <div style={styles.videoLabel}>
        Vídeo: {watchOnlyVideoQuestionItem.question}
      </div>

      <div style={styles.videoPlayerWrap}>
        {watchOnlyVideoUrl && !videoLoadFailed ? (
          <>
            <div style={styles.videoControlsWrap(true)}>
              <div style={styles.videoPrimaryActions}>
                <button
                  type="button"
                  onClick={handleToggleVideoPlayback}
                  style={styles.videoControlPrimary}
                >
                  {isVideoPlaying ? (
                    <PauseRoundedIcon style={{ fontSize: 20 }} />
                  ) : (
                    <PlayArrowRoundedIcon style={{ fontSize: 20 }} />
                  )}
                  {isVideoPlaying ? 'Pausar' : 'Reproduzir'}
                </button>

                {!isNativeIosSafariVideo && (
                  <button
                    type="button"
                    onClick={handleToggleFullscreen}
                    style={styles.videoControlSecondary}
                  >
                    {isVideoFullscreen ? (
                      <FullscreenExitRoundedIcon style={{ fontSize: 20 }} />
                    ) : (
                      <FullscreenRoundedIcon style={{ fontSize: 20 }} />
                    )}
                    {isVideoFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                  </button>
                )}
              </div>

              <div style={styles.videoVolumeGroup(false, isMobile)}>
                <button
                  type="button"
                  onClick={handleToggleMute}
                  style={styles.videoControlSecondary}
                >
                  {videoMuted || videoVolume === 0 ? (
                    <VolumeOffRoundedIcon style={{ fontSize: 20 }} />
                  ) : (
                    <VolumeUpRoundedIcon style={{ fontSize: 20 }} />
                  )}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={videoMuted ? 0 : videoVolume}
                  onChange={handleVolumeChange}
                  style={styles.videoVolumeSlider(false)}
                  aria-label="Volume do vídeo"
                />
              </div>

              <div style={styles.videoSpeedWrap}>
                <div style={styles.videoSpeedLabel}>Velocidade permitida</div>
                <div style={styles.videoSpeedOptions}>
                  {PLAYBACK_RATES.map(rate => (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => handlePlaybackRateChange(rate)}
                      style={styles.videoSpeedButton(
                        videoPlaybackRate === rate
                      )}
                    >
                      {rate}x
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.videoMetaInfo}>
                <div style={styles.videoMetaLabel}>
                  {videoReviewMode
                    ? 'Modo de revisão do vídeo'
                    : 'Avanço de vídeo desativado'}
                </div>
                <div style={styles.videoMetaTime}>{videoTimeLabel}</div>
              </div>
            </div>

            <div
              ref={videoShellRef}
              style={styles.videoShell(isVideoFullscreen, isMobile)}
              onClick={!videoReviewMode ? handleVideoSurfaceClick : undefined}
              onKeyDown={
                !videoReviewMode
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleVideoSurfaceClick();
                      }
                    }
                  : undefined
              }
              onMouseMove={
                isVideoFullscreen ? revealFullscreenControls : undefined
              }
              role={!videoReviewMode ? 'button' : undefined}
              tabIndex={!videoReviewMode ? 0 : undefined}
            >
              {videoLoading && (
                <div style={styles.videoLoadingOverlay}>
                  <div style={styles.videoLoadingBox}>
                    <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
                    <span>Carregando vídeo...</span>
                  </div>
                </div>
              )}

              {!videoLoading &&
                !videoLoadFailed &&
                !isVideoPlaying &&
                videoCurrentTime <= 0 && (
                  <div
                    style={styles.videoIdleOverlay(showVideoFallbackCover)}
                    onClick={event => {
                      event.stopPropagation();
                      handleStartVideoFromOverlay();
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleStartVideoFromOverlay();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Iniciar reprodução do vídeo"
                  >
                    {showVideoFallbackCover && (
                      <div style={styles.videoIdleBackdrop}>
                        <div style={styles.videoIdleBackdropBadge}>
                          Conteúdo do treinamento
                        </div>
                        <div style={styles.videoIdleBackdropText}>
                          Toque em reproduzir para iniciar o conteúdo.
                        </div>
                      </div>
                    )}
                    <div style={styles.videoIdleBox}>
                      <PlayCircleOutlineOutlinedIcon
                        style={{ fontSize: 44, opacity: 0.96 }}
                      />
                      <div style={styles.videoIdleTitle}>
                        Vídeo pronto para iniciar
                      </div>
                      <div style={styles.videoIdleSubtitle}>
                        O avanço continua bloqueado até assistir todo o
                        conteúdo.
                      </div>
                    </div>
                  </div>
                )}

              <video
                key={`${
                  watchOnlyVideoQuestionItem?.id || currentVideoIndex
                }-${watchOnlyVideoUrl}`}
                ref={videoRef}
                controls={videoReviewMode}
                disablePictureInPicture
                playsInline
                preload="metadata"
                tabIndex={-1}
                onLoadStart={handleVideoLoadStart}
                onLoadedData={handleVideoLoadedData}
                onTimeUpdate={handleVideoTimeUpdate}
                onSeeking={handleVideoSeeking}
                onEnded={handleVideoEnded}
                onLoadedMetadata={handleVideoLoadedMetadata}
                onCanPlay={handleVideoCanPlay}
                onCanPlayThrough={handleVideoCanPlay}
                onPlaying={handleVideoCanPlay}
                onWaiting={handleVideoWaiting}
                onSuspend={handleVideoSuspend}
                onError={handleVideoError}
                onVolumeChange={handleVideoVolumeChange}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onClick={
                  !videoReviewMode
                    ? event => {
                        event.stopPropagation();
                        handleVideoSurfaceClick();
                      }
                    : undefined
                }
                style={styles.videoPlayer(
                  isVideoFullscreen,
                  showVideoFallbackCover
                )}
              >
                <source src={watchOnlyVideoUrl} />
                <track kind="captions" />
              </video>

              {!isNativeIosSafariVideo &&
                isVideoFullscreen &&
                showFullscreenControls && (
                  <div style={styles.videoOverlayControls(isMobile)}>
                    <div style={styles.videoOverlayLeft}>
                      <button
                        type="button"
                        onClick={handleToggleVideoPlayback}
                        style={styles.videoControlPrimary}
                      >
                        {isVideoPlaying ? (
                          <PauseRoundedIcon style={{ fontSize: 20 }} />
                        ) : (
                          <PlayArrowRoundedIcon style={{ fontSize: 20 }} />
                        )}
                        {isVideoPlaying ? 'Pausar' : 'Reproduzir'}
                      </button>

                      <div style={styles.videoVolumeGroup(true, isMobile)}>
                        <button
                          type="button"
                          onClick={handleToggleMute}
                          style={styles.videoControlSecondaryDark}
                        >
                          {videoMuted || videoVolume === 0 ? (
                            <VolumeOffRoundedIcon style={{ fontSize: 20 }} />
                          ) : (
                            <VolumeUpRoundedIcon style={{ fontSize: 20 }} />
                          )}
                        </button>

                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={videoMuted ? 0 : videoVolume}
                          onChange={handleVolumeChange}
                          style={styles.videoVolumeSlider(true)}
                          aria-label="Volume do vídeo"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleToggleFullscreen}
                        style={styles.videoControlSecondaryDark}
                      >
                        <FullscreenExitRoundedIcon style={{ fontSize: 20 }} />
                        Sair da tela cheia
                      </button>
                    </div>

                    <div style={styles.videoOverlayRight(isMobile)}>
                      <div style={styles.videoOverlaySpeedLabel}>
                        Velocidade
                      </div>
                      <div style={styles.videoSpeedOptions}>
                        {PLAYBACK_RATES.map(rate => (
                          <button
                            key={`fullscreen-${rate}`}
                            type="button"
                            onClick={() => handlePlaybackRateChange(rate)}
                            style={styles.videoSpeedButtonDark(
                              videoPlaybackRate === rate
                            )}
                          >
                            {rate}x
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </>
        ) : (
          <div style={styles.videoUnavailable}>
            <Text color="gray">Vídeo não disponível.</Text>
          </div>
        )}
      </div>

      <div style={styles.videoProgressWrap}>
        <div style={styles.videoProgressRow}>
          <div style={styles.videoProgressTitle}>Progresso do video</div>
          <div style={styles.videoProgressValue}>{videoProgressLabel}</div>
        </div>
        <div style={styles.videoProgressTrack}>
          <div style={styles.videoProgressFill(videoProgressPct)} />
        </div>
      </div>

      <div style={styles.videoInfoBox}>
        <div style={styles.videoInfoTitle}>
          <InfoOutlinedIcon style={{ fontSize: 16 }} />
          Próximos passos
        </div>
        <div style={styles.videoInfoText}>
          {videoReviewMode
            ? 'Modo de revisao liberado. Agora voce pode navegar livremente pelo video para consultar qualquer trecho antes de enviar suas respostas.'
            : !isLastVideo
            ? `Conteúdo concluído. Ao prosseguir, você assistirá ao vídeo ${
                currentVideoIndex + 2
              } de ${totalVideos}.`
            : hasOnlyVideoTraining
            ? 'Conteúdo concluído. Ao prosseguir, o treinamento será finalizado automaticamente.'
            : videoGateCompleted
            ? `Conteúdo concluído. Você pode responder ${
                total || 0
              } pergunta(s) para completar o treinamento.`
            : `Após visualizar este conteúdo, você responderá ${
                total || 0
              } pergunta(s) para completar o treinamento.`}
        </div>
      </div>

      <div style={styles.videoFooterActions}>
        {!answered && (
          <Button
            onClick={handleBackToIdentification}
            outlined
            style={styles.videoBackBtn(isMobile)}
          >
            Voltar para Identificação
          </Button>
        )}

        <Button
          onClick={handleContinueAfterVideo}
          disabled={answered || (!videoReviewMode && !videoGateCompleted)}
          style={styles.videoContinueBtn(isMobile)}
        >
          <div style={styles.videoContinueContent}>
            {answered ? (
              <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
            ) : null}
            <span>
              {answered
                ? 'Treinamento concluído'
                : videoReviewMode
                ? 'Voltar para Perguntas'
                : !isLastVideo
                ? 'Próximo Vídeo'
                : hasOnlyVideoTraining
                ? 'Concluir Treinamento'
                : 'Prosseguir para Perguntas'}
            </span>
          </div>
        </Button>
      </div>
    </div>
  );
}
